import { useState, type FormEvent } from 'react';
import { AppIcon } from '../app/AppIcon.js';
import { motion } from 'motion/react';
import { ApiError, auth } from '../api/client.js';

/**
 * "Şifremi unuttum".
 *
 * ## Why the second step is never skipped
 *
 * The server answers the same way whether or not the account exists, so this
 * screen advances to "enter the code" unconditionally. It is tempting to save
 * the user a step when there is no account to recover — and that shortcut is
 * exactly the membership oracle the uniform answer exists to prevent. A wrong
 * username costs one wasted screen; leaking who has an account costs everyone.
 *
 * The wording therefore promises nothing: "if there is an account with a
 * verified address, a code is on its way". Not "we sent you a code".
 */

const EASE_SWIM = [0.16, 1, 0.3, 1] as const;
const MIN_PASSWORD = 12;

type Step = 'ask' | 'redeem' | 'done';

export function RecoveryFlow({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState<Step>('ask');
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function describe(caught: unknown): string {
    if (caught instanceof ApiError) {
      return caught.code === 'RATE_LIMITED'
        ? `Çok fazla deneme. ${caught.retryAfter ?? 60} saniye sonra tekrar dene.`
        : caught.message;
    }
    return 'Beklenmeyen bir hata oldu.';
  }

  async function request(event: FormEvent) {
    event.preventDefault();
    if (username.length < 2 || busy) return;

    setBusy(true);
    setError(null);
    try {
      await auth.forgotPassword(username);
      setStep('redeem');
    } catch (caught) {
      // Only transport and rate-limit failures reach here; the endpoint itself
      // succeeds regardless of what it found.
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  }

  async function redeem(event: FormEvent) {
    event.preventDefault();
    if (code.length < 6 || password.length < MIN_PASSWORD || busy) return;

    setBusy(true);
    setError(null);
    try {
      await auth.resetPassword(username, code, password);
      setStep('done');
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
      setPassword('');
    }
  }

  return (
    <motion.div
      className="auth-card"
      initial={{ opacity: 0, y: 16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, ease: EASE_SWIM }}
    >
      <button type="button" className="auth-back" onClick={onBack}>
        <AppIcon name="arrowLeft" size={14} hole="transparent" />
        Girişe dön
      </button>

      {step === 'ask' ? (
        <form onSubmit={request} noValidate>
          <div className="auth-step-head">
            <h2>Parolanı sıfırla</h2>
            <p>Hesabındaki doğrulanmış e-posta adresine altı haneli bir kod göndereceğiz.</p>
          </div>

          <label className="field">
            <span className="field-label">Kullanıcı adı veya e-posta</span>
            <input
              className="field-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="aksolotl"
            />
          </label>

          {error !== null ? (
            <div className="auth-error" role="alert">
              <AppIcon name="warning" size={15} hole="transparent" />
              {error}
            </div>
          ) : null}

          <button type="submit" className="auth-submit" disabled={username.length < 2 || busy}>
            {busy ? 'Gönderiliyor…' : 'Kod gönder'}
            <span className="send-well">
              <AppIcon name="arrowRight" size={14} hole="transparent" />
            </span>
          </button>
        </form>
      ) : null}

      {step === 'redeem' ? (
        <form onSubmit={redeem} noValidate>
          <div className="auth-step-head">
            <AppIcon name="mail" size={22} hole="transparent" style={{ color: 'var(--tone-encrypted)' }} />
            <h2>Kodu gir</h2>
            {/*
              Deliberately conditional. Stating that a message was sent would
              confirm the account exists to anyone who typed a username.
            */}
            <p>
              Doğrulanmış bir adresi olan bir hesap varsa kod yolda. Kod 15 dakika geçerli ve bir
              kez kullanılır.
            </p>
          </div>

          <label className="field">
            <span className="field-label">Kod</span>
            <input
              className="field-input field-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
            />
          </label>

          <label className="field">
            <span className="field-label">Yeni parola</span>
            <input
              className="field-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={`En az ${MIN_PASSWORD} karakter`}
            />
            {password !== '' && password.length < MIN_PASSWORD ? (
              <span className="field-problem">En az {MIN_PASSWORD} karakter.</span>
            ) : null}
          </label>

          {error !== null ? (
            <div className="auth-error" role="alert">
              <AppIcon name="warning" size={15} hole="transparent" />
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            className="auth-submit"
            disabled={code.length < 6 || password.length < MIN_PASSWORD || busy}
          >
            {busy ? 'Sıfırlanıyor…' : 'Parolayı değiştir'}
            <span className="send-well">
              <AppIcon name="arrowRight" size={14} hole="transparent" />
            </span>
          </button>

          <button type="button" className="auth-secondary" onClick={() => setStep('ask')}>
            Kod gelmedi mi? Yeniden dene
          </button>
        </form>
      ) : null}

      {step === 'done' ? (
        <div className="auth-step-head auth-done">
          <h2>Parolan değişti</h2>
          {/*
            Said plainly, because it is a surprise otherwise: the reset signed
            out every device. That is the point of a reset — the reason someone
            resets a password is that they think another session is not theirs.
          */}
          <p>
            Güvenlik için tüm cihazlardaki oturumlar kapatıldı. Yeni parolanla yeniden giriş yap.
          </p>
          <button type="button" className="auth-submit" onClick={onBack}>
            Giriş yap
            <span className="send-well">
              <AppIcon name="arrowRight" size={14} hole="transparent" />
            </span>
          </button>
        </div>
      ) : null}
    </motion.div>
  );
}
